import API_ENDPOINTS from '../config/apiConfig';
import { FASTIFY_BASE_URL } from '../config/apiConfig';
import { api } from './api';

const DASHBOARD_FACT_REFRESH_TIMEOUT_MS = 60 * 60 * 1000;

// Cache for organization data
const orgCache = {
  allOrganizations: null,
  organizationById: {},
  organizationByCode: {},
  timestamp: null,
  cacheExpiry: 5 * 60 * 1000, // 5 minutes
  invalidateCache: () => {
    orgCache.allOrganizations = null;
    orgCache.organizationById = {};
    orgCache.organizationByCode = {};
    orgCache.timestamp = null;
  }
};

// Get all organizations from the dedicated endpoint
export const getOrganizations = async () => {
  try {
    // Check cache first
    const now = Date.now();
    if (orgCache.allOrganizations && 
        orgCache.timestamp && 
        (now - orgCache.timestamp) < orgCache.cacheExpiry) {

      return orgCache.allOrganizations;
    }
    

    // Call the /organizations endpoint
    const response = await api.get(API_ENDPOINTS.ORGANIZATIONS);

    const responseData = response.data;
    const organizations = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.body)
        ? responseData.body
        : [];
    
    // Update cache
    orgCache.allOrganizations = organizations;
    orgCache.timestamp = now;
    
    // Also cache individual organizations
    if (Array.isArray(organizations)) {
      organizations.forEach(org => {
        if (org && org.id) {
          orgCache.organizationById[org.id] = org;
        }
        if (org && org.code) {
          orgCache.organizationByCode[org.code] = org;
        }
      });
    }
    
    return organizations;
  } catch (error) {
    console.error('Error in getOrganizations:', error);
    return orgCache.allOrganizations || []; // Return cached data or empty array
  }
};

// Get organization by ID - get all orgs and filter to find the one with matching ID
export const getOrganizationById = async (id) => {
  if (!id) {
    console.warn('getOrganizationById called without id parameter');
    return null;
  }
  
  // Check cache first (uses TTL from getOrganizations)
  if (orgCache.organizationById[id]) {
    return orgCache.organizationById[id];
  }

  // Get all organizations and find the one with matching ID
  const allOrgs = await getOrganizations();
  const org = allOrgs.find(o => o.id === id);

  if (!org) {
    console.warn(`Organization with ID ${id} not found`);
    return null;
  }

  return org;
};

// Get organization by code - get all orgs and filter to find the one with matching code
export const getOrganizationByCode = async (code) => {
  if (!code) {
    console.warn('getOrganizationByCode called without code parameter');
    return null;
  }

  // Check cache first (uses TTL from getOrganizations)
  if (orgCache.organizationByCode[code]) {
    return orgCache.organizationByCode[code];
  }

  // Get all organizations and find the one with matching code
  const allOrgs = await getOrganizations();
  const org = allOrgs.find(o => o.code === code);
  
  if (!org) {
    console.warn(`Organization with code ${code} not found`);
    return null;
  }
  
  return org;
};

export const createOrganization = async (orgData) => {
  try {
    const response = await api.post(API_ENDPOINTS.ORGANIZATIONS, orgData);
    orgCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in createOrganization:', error);
    throw error;
  }
};

export const updateOrganization = async (orgData) => {
  try {
    const response = await api.put(API_ENDPOINTS.ORGANIZATIONS, orgData);
    orgCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in updateOrganization:', error);
    throw error;
  }
};

// Fastify-targeted org load/save used by the admin Refresh Management page.
// The legacy func host (BASE_URL) doesn't hot-reload and lags behind deploys,
// which silently dropped new refresh-schedule fields (interval, days-forward);
// fastify is the modern path and always current.
export const getRefreshOrganizations = async () => {
  const { FASTIFY_BASE_URL } = await import('../config/apiConfig');
  const response = await api.get(`${FASTIFY_BASE_URL}/organizations`);
  return Array.isArray(response.data) ? response.data : (response.data?.organizations || []);
};

export const saveRefreshSchedule = async (orgData) => {
  const { FASTIFY_BASE_URL } = await import('../config/apiConfig');
  const response = await api.put(`${FASTIFY_BASE_URL}/organizations`, orgData);
  orgCache.invalidateCache();
  return response.data;
};

export const deleteOrganization = async (id) => {
  try {
    await api.delete(`${API_ENDPOINTS.ORGANIZATIONS}?id=${id}`);
    orgCache.invalidateCache();
    return true;
  } catch (error) {
    console.error('Error in deleteOrganization:', error);
    throw error;
  }
};

export const getCurrencies = async () => {
  const response = await api.get('/currencies');
  return Array.isArray(response.data) ? response.data : [];
};

export const upsertCurrency = async (currency) => {
  const response = await api.post('/currencies', currency);
  return response.data;
};

export const getCurrencyPairs = async (orgId) => {
  const response = await api.get(`/currencyPairs?orgId=${orgId}`);
  return Array.isArray(response.data) ? response.data : [];
};

export const createCurrencyPair = async (pair) => {
  const response = await api.post('/currencyPairs', pair);
  return response.data;
};

export const updateCurrencyPair = async (pair) => {
  const response = await api.put('/currencyPairs', pair);
  return response.data;
};

export const getCurrencyRates = async ({ orgId, pairId, granularity }) => {
  const params = new URLSearchParams({ orgId: String(orgId) });
  if (pairId) params.set('pairId', String(pairId));
  if (granularity) params.set('granularity', granularity);
  const response = await api.get(`/currencyRates?${params.toString()}`);
  return Array.isArray(response.data) ? response.data : [];
};

export const saveCurrencyRate = async (rate) => {
  const response = await api.put('/currencyRates', rate);
  return response.data;
};

/**
 * Start a dashboard + report fact refresh as a BACKGROUND task (fastify).
 * Returns immediately ({ started: true }); results land in the refresh
 * history and the org's dashboardRefreshLastStatus.
 */
export const startFactRefresh = async (payload) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/factRefresh/run`, payload);
  return response.data;
};

/**
 * Stop the org's running refresh, wherever it runs (fastify). The table in
 * flight rolls back; tables already refreshed keep their new data.
 * 409 when nothing is running.
 */
export const cancelFactRefresh = async (orgId) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/factRefresh/cancel`, { organizationId: orgId });
  return response.data;
};

// Live progress for an in-flight background refresh: { running, startedAt,
// elapsedMs, tablesDone, tablesTotal, rowsInserted, rowsDeleted, lastTable }.
export const getFactRefreshStatus = async (orgId) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/factRefresh/status`, { params: { orgId } });
  return response.data;
};

export const reloadDashboardData = async ({
  organizationId,
  locationId = null,
  historyMonths = 25,
  futureMonths = 13,
  historyDays = null,
  futureDays = null
}) => {
  try {
    const response = await api.post(API_ENDPOINTS.DASHBOARD_FACT_REFRESH, {
      organizationId,
      locationId,
      historyMonths,
      futureMonths,
      historyDays,
      futureDays
    }, {
      timeout: DASHBOARD_FACT_REFRESH_TIMEOUT_MS
    });
    return response.data;
  } catch (error) {
    console.error('Error in reloadDashboardData:', error);
    throw error;
  }
};

// ── Admin → Organizations (throwing variants) ───────────────────────────────
// The helpers above swallow errors for the many pages that just need a list.
// The admin Organizations pages need to show real error states, so these
// never fall back to [] — callers catch and render the failure.

/** Pull a readable message out of an axios error. */
export const apiErrorMessage = (error, fallback = 'Something went wrong') => (
  error?.response?.data?.error
  || error?.response?.data?.message
  || (typeof error?.response?.data === 'string' && error.response.data.length < 300 ? error.response.data : null)
  || error?.message
  || fallback
);

/**
 * One-call admin overview: per org settings, schedule, last refresh outcome,
 * location/user counts, recent runs, fact states and any run in flight.
 * Pass orgId to summarize a single organization.
 */
export const fetchOrganizationsSummary = async ({ orgId } = {}) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/admin/organizationsSummary`, {
    params: orgId ? { orgId } : undefined,
  });
  return response.data || { organizations: [], warnings: [] };
};

/** Partial update: send only the fields that changed. */
export const patchOrganization = async (id, fields) => {
  const response = await api.put(API_ENDPOINTS.ORGANIZATIONS, { id, ...fields });
  orgCache.invalidateCache();
  return response.data;
};

/** The org-config document for one org, or null when none exists. */
export const fetchOrgConfigForOrg = async (orgId) => {
  const response = await api.get(API_ENDPOINTS.GET_ORG_CONFIG, { params: { orgId } });
  const docs = Array.isArray(response.data) ? response.data : [];
  return docs.find((doc) => doc && !doc.isDeleted) || docs[0] || null;
};

/**
 * The org's GL link templates — invoice and journal entry URLs built from a
 * GL line — plus placeholders, canEdit and whether the accounting sync links.
 */
export const getGlLinks = async (orgId) => (await api.get(`${FASTIFY_BASE_URL}/orgConfig/glLinks`, { params: { orgId } })).data;

/** Org admins only. glLinks: { invoiceUrlTemplate, journalUrlTemplate, systemName } (blank clears). */
export const saveGlLinks = async (orgId, glLinks) => (await api.put(`${FASTIFY_BASE_URL}/orgConfig/glLinks`, { orgId, glLinks })).data;

/** Soft-remove a currency pair (isActive = 0; rate history is kept). */
export const removeCurrencyPair = async ({ id, orgId }) => {
  const response = await api.delete('/currencyPairs', { params: { id, orgId } });
  return response.data;
};

/** One page of fact refresh history rows, newest first. */
export const fetchRefreshHistory = async ({ orgId, limit = 300, beforeId } = {}) => {
  const params = { orgId, limit };
  if (beforeId) params.beforeId = beforeId;
  const response = await api.get(`${FASTIFY_BASE_URL}/factRefreshHistory`, { params });
  const data = response.data || {};
  return {
    rows: Array.isArray(data.rows) ? data.rows : [],
    hasMore: Boolean(data.hasMore),
    nextBeforeId: data.nextBeforeId || null,
  };
};
